<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useLanguageStore } from '../stores/languageStore'
import { ElMessage, ElNotification } from 'element-plus'
import apiService from '../services/apiService'

const languageStore = useLanguageStore()
const authStore = useAuthStore()

// Ana state'ler
const commentText = ref('')
const targetType = ref('latest_video')
const selectedChannels = ref<string[]>([])
const isLoading = ref(false)
const isTestMode = ref(false)
const dailyLimit = ref(20)
const delaySeconds = ref(30)



// Popüler kanal arama state'leri
const isSearchingChannels = ref(false)
const searchResults = ref<any>(null)
const minSubscribers = ref(100000)
const maxSubscribers = ref(1000000)
const maxResults = ref(5)

// Kategori filtreleme state'leri
const categories = ref<any[]>([])
const selectedCategory = ref('')
const filteredChannels = ref<any[]>([])
const isLoadingCategories = ref(false)
const isLoadingFilteredChannels = ref(false)

// Topic kategorileri state'leri
const topicCategories = ref<string[]>([])
const isLoadingTopicCategories = ref(false)

// YouTube kategorileri state'leri
const youtubeCategories = ref<any[]>([])
const isLoadingYouTubeCategories = ref(false)

// Kanal listesi
const channels = ref<any[]>([])
const isLoadingChannels = ref(false)

// Yorum geçmişi
const commentHistory = ref<any[]>([])
const isLoadingHistory = ref(false)

// İstatistikler
const stats = ref({
  totalComments: 0,
  successRate: 0,
  activeChannels: 0,
  todayComments: 0
})

// OAuth2 Setup Modal
const showOAuthSetup = ref(false)

// Computed
const characterCount = computed(() => commentText.value.length)
const isValidComment = computed(() => commentText.value.length > 10 && commentText.value.length <= 500)

// Methods
const loadChannels = async () => {
  try {
    isLoadingChannels.value = true
    const response = await apiService.get('/api/channels')
    channels.value = response.data || []
  } catch (error) {
    console.error('Kanallar yüklenirken hata:', error)
    ElMessage.error('Kanallar yüklenemedi')
  } finally {
    isLoadingChannels.value = false
  }
}

const loadCommentHistory = async () => {
  try {
    isLoadingHistory.value = true
    const response = await apiService.get('/api/admin/youtube-comments')
    commentHistory.value = response.data || []
  } catch (error) {
    console.error('Yorum geçmişi yüklenirken hata:', error)
    ElMessage.error('Yorum geçmişi yüklenemedi')
  } finally {
    isLoadingHistory.value = false
  }
}

const loadStats = async () => {
  try {
    const response = await apiService.get('/api/admin/youtube-comments/stats')
    stats.value = response.data || stats.value
  } catch (error) {
    console.error('İstatistikler yüklenirken hata:', error)
  }
}

const sendComments = async () => {
  if (!isValidComment.value) {
    ElMessage.warning('Yorum metni 10-500 karakter arasında olmalıdır')
    return
  }

  if (selectedChannels.value.length === 0) {
    ElMessage.warning('En az bir kanal seçmelisiniz')
    return
  }

  try {
    isLoading.value = true
    
    const payload = {
      comment_text: commentText.value,
      target_type: targetType.value,
      channel_ids: selectedChannels.value,
      settings: {
        daily_limit: dailyLimit.value,
        test_mode: isTestMode.value,
        delay_seconds: delaySeconds.value
      }
    }

    const response = await apiService.post('/api/admin/youtube-comments', payload)
    
    if (response && response.success) {
      ElNotification({
        title: 'Başarılı',
        message: 'Yorum görevleri oluşturuldu',
        type: 'success'
      })
      
      // Formu temizle
      commentText.value = ''
      selectedChannels.value = []
      
      // Geçmişi yenile
      await loadCommentHistory()
      await loadStats()
    } else {
      ElMessage.error('Yorum gönderilemedi: Geçersiz yanıt')
    }
  } catch (error) {
    console.error('Yorum gönderilirken hata:', error)
    ElMessage.error('Yorum gönderilemedi')
  } finally {
    isLoading.value = false
  }
}

const clearForm = () => {
  commentText.value = ''
  selectedChannels.value = []
  targetType.value = 'latest_video'
  isTestMode.value = false
  dailyLimit.value = 20
  delaySeconds.value = 30
}

const findPopularChannels = async () => {
  try {
    isSearchingChannels.value = true
    searchResults.value = null
    
    const response = await apiService.post('/api/admin/find-popular-channels', {
      minSubscribers: minSubscribers.value,
      maxSubscribers: maxSubscribers.value,
      maxResults: maxResults.value
    })
    
    searchResults.value = response.data
    
    ElNotification({
      title: 'Başarılı',
      message: response.data.message,
      type: 'success'
    })
    
    // Kanal listesini ve kategorileri yenile
    await Promise.all([
      loadChannels(),
      loadCategories()
    ])
    
  } catch (error) {
    console.error('Popüler kanal arama hatası:', error)
    ElMessage.error('Popüler kanal arama başarısız')
  } finally {
    isSearchingChannels.value = false
  }
}

const loadCategories = async () => {
  try {
    isLoadingCategories.value = true
    const response = await apiService.get('/api/admin/channel-categories')
    categories.value = response.categories || []
  } catch (error) {
    console.error('Kategoriler yüklenirken hata:', error)
    ElMessage.error('Kategoriler yüklenemedi')
  } finally {
    isLoadingCategories.value = false
  }
}



const loadFilteredChannels = async () => {
  if (!selectedCategory.value) {
    filteredChannels.value = []
    return
  }

  try {
    isLoadingFilteredChannels.value = true
    console.log('Loading filtered channels for category:', selectedCategory.value)
    
    const response = await apiService.get(`/api/admin/channels-by-category?category=${encodeURIComponent(selectedCategory.value)}&minSubscribers=${minSubscribers.value}&limit=200`)
    
    console.log('Filtered channels response:', response)
    
    filteredChannels.value = response.data?.channels || response.channels || []
    console.log('Filtered channels count:', filteredChannels.value.length)
  } catch (error) {
    console.error('Filtrelenmiş kanallar yüklenirken hata:', error)
    ElMessage.error('Filtrelenmiş kanallar yüklenemedi')
  } finally {
    isLoadingFilteredChannels.value = false
  }
}

const onCategoryChange = () => {
  selectedChannels.value = [] // Seçili kanalları temizle
  loadFilteredChannels()
}

// Topic kategorilerini yükle
const loadTopicCategories = async () => {
  isLoadingTopicCategories.value = true
  try {
    const response = await apiService.get('/api/admin/channel-topic-categories')
    topicCategories.value = response.data.categories || []
    console.log('Topic kategorileri:', topicCategories.value)
    ElMessage.success(`${topicCategories.value.length} kategori bulundu`)
  } catch (error) {
    console.error('Topic kategorileri yüklenirken hata:', error)
    ElMessage.error('Topic kategorileri yüklenirken hata oluştu')
  } finally {
    isLoadingTopicCategories.value = false
  }
}

// YouTube kategorilerini yükle
const loadYouTubeCategories = async () => {
  isLoadingYouTubeCategories.value = true
  try {
    const response = await apiService.get('/api/admin/youtube-categories')
    console.log('YouTube categories response:', response)
    youtubeCategories.value = response.data?.categories || []
    console.log('YouTube kategorileri:', youtubeCategories.value)
    ElMessage.success(`${youtubeCategories.value.length} YouTube kategorisi bulundu`)
  } catch (error) {
    console.error('YouTube kategorileri yüklenirken hata:', error)
    ElMessage.error('YouTube kategorileri yüklenirken hata oluştu')
  } finally {
    isLoadingYouTubeCategories.value = false
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'posted': return 'text-green-600'
    case 'failed': return 'text-red-600'
    case 'pending': return 'text-yellow-600'
    case 'cancelled': return 'text-gray-600'
    default: return 'text-gray-600'
  }
}

const getStatusText = (status: string) => {
  switch (status) {
    case 'posted': return 'Gönderildi'
    case 'failed': return 'Başarısız'
    case 'pending': return 'Bekliyor'
    case 'cancelled': return 'İptal Edildi'
    default: return status
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('tr-TR')
}

// Lifecycle
onMounted(async () => {
  // Önce authentication'ı kontrol et
  await authStore.checkUser()
  
  // Debug için auth durumunu logla
  console.log('Auth status:', {
    user: authStore.user,
    session: authStore.getSession(),
    isAuthenticated: authStore.isAuthenticated
  })
  
  // Eğer kullanıcı yoksa veya admin değilse, işlemleri yapma
  if (!authStore.user) {
    ElMessage.error('Giriş yapmanız gerekiyor')
    return
  }
  
  // Admin kontrolü
  const email = (authStore.user as any)?.email || ''
  const adminEmail = (import.meta as any).env?.VITE_ADMIN_EMAIL || 'metinarab@gmail.com'
  console.log('Admin check:', { email, adminEmail, isAdmin: email.toLowerCase() === String(adminEmail).toLowerCase() })
  
  if (email.toLowerCase() !== String(adminEmail).toLowerCase()) {
    ElMessage.error('Bu sayfaya erişim yetkiniz yok')
    return
  }
  
  await Promise.all([
    loadChannels(),
    loadCommentHistory(),
    loadStats(),
    loadCategories()
  ])
})
</script>

<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-50 to-white">
    <!-- Header -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
      <div class="bg-white rounded-xl shadow-lg p-6 lg:p-8">
        <div class="flex items-center justify-between mb-8">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">YouTube Yorum Yönetimi</h1>
            <p class="text-gray-600 mt-2">Seçilen kanalların son videolarına otomatik yorum gönderin</p>
          </div>
          <div class="flex gap-3">
            <button
              @click="showOAuthSetup = true"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
              </svg>
              OAuth2 Kurulum Rehberi
            </button>
          </div>
        </div>

        <!-- İstatistikler -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
            <div class="flex items-center">
              <div class="p-2 bg-white bg-opacity-20 rounded-lg">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm opacity-90">Toplam Yorum</p>
                <p class="text-2xl font-bold">{{ stats.totalComments }}</p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
            <div class="flex items-center">
              <div class="p-2 bg-white bg-opacity-20 rounded-lg">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm opacity-90">Başarı Oranı</p>
                <p class="text-2xl font-bold">%{{ stats.successRate }}</p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
            <div class="flex items-center">
              <div class="p-2 bg-white bg-opacity-20 rounded-lg">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm opacity-90">Aktif Kanal</p>
                <p class="text-2xl font-bold">{{ stats.activeChannels }}</p>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
            <div class="flex items-center">
              <div class="p-2 bg-white bg-opacity-20 rounded-lg">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm opacity-90">Bugün</p>
                <p class="text-2xl font-bold">{{ stats.todayComments }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Popüler Kanal Arama -->
        <div class="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 mb-8 border border-purple-200">
          <h2 class="text-xl font-semibold mb-6 text-purple-800">Popüler Kanal Keşfi</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Minimum Abone Sayısı
              </label>
              <input
                v-model.number="minSubscribers"
                type="number"
                min="10000"
                max="10000000"
                step="10000"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Maksimum Abone Sayısı
              </label>
              <input
                v-model.number="maxSubscribers"
                type="number"
                min="10000"
                max="10000000"
                step="10000"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Maksimum Sonuç
              </label>
              <input
                v-model.number="maxResults"
                type="number"
                min="10"
                max="200"
                step="10"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
            </div>
            
            <div class="flex items-end gap-2">
              <button
                @click="findPopularChannels"
                :disabled="isSearchingChannels"
                class="flex-1 px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <div v-if="isSearchingChannels" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {{ isSearchingChannels ? 'Aranıyor...' : `${(minSubscribers / 1000).toFixed(0)}K-${(maxSubscribers / 1000).toFixed(0)}K ${maxResults} Kanal Bul` }}
              </button>
              <button
                @click="loadYouTubeCategories"
                :disabled="isLoadingYouTubeCategories"
                class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <div v-if="isLoadingYouTubeCategories" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {{ isLoadingYouTubeCategories ? 'Yükleniyor...' : 'YouTube Kategorileri' }}
              </button>
            </div>
          </div>
          
          <!-- Arama Sonuçları -->
          <div v-if="searchResults" class="bg-white rounded-lg p-4 border border-purple-200">
            <h3 class="text-lg font-semibold mb-3 text-purple-800">Arama Sonuçları</h3>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div class="bg-green-50 p-3 rounded-lg">
                <div class="font-semibold text-green-800">Bulunan Kanal</div>
                <div class="text-2xl font-bold text-green-600">{{ searchResults.found }}</div>
              </div>
              <div class="bg-blue-50 p-3 rounded-lg">
                <div class="font-semibold text-blue-800">Yeni Eklenen</div>
                <div class="text-2xl font-bold text-blue-600">{{ searchResults.added }}</div>
              </div>
              <div class="bg-yellow-50 p-3 rounded-lg">
                <div class="font-semibold text-yellow-800">Zaten Mevcut</div>
                <div class="text-2xl font-bold text-yellow-600">{{ searchResults.skipped }}</div>
              </div>
              <div class="bg-purple-50 p-3 rounded-lg">
                <div class="font-semibold text-purple-800">Minimum Abone</div>
                <div class="text-2xl font-bold text-purple-600">{{ (minSubscribers / 1000).toFixed(0) }}K</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Kategori Filtreleme -->
        <div class="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-8 border border-green-200">
          <h2 class="text-xl font-semibold mb-6 text-green-800">Kategori Bazlı Kanal Seçimi</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Kategori Seçin
              </label>
              <select
                v-model="selectedCategory"
                @change="onCategoryChange"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Tüm Kategoriler</option>
                <option
                  v-for="category in categories"
                  :key="category.name"
                  :value="category.name"
                >
                  {{ category.name }} ({{ category.count }})
                </option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Minimum Abone Sayısı
              </label>
              <input
                v-model.number="minSubscribers"
                type="number"
                min="10000"
                max="10000000"
                step="10000"
                @change="onCategoryChange"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
            </div>
            
            <div class="flex items-end">
              <button
                @click="loadFilteredChannels"
                :disabled="!selectedCategory || isLoadingFilteredChannels"
                class="w-full px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <div v-if="isLoadingFilteredChannels" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {{ isLoadingFilteredChannels ? 'Yükleniyor...' : 'Kanalları Getir' }}
              </button>
            </div>
          </div>
          
          <!-- Filtrelenmiş Kanal Listesi -->
          <div v-if="filteredChannels.length > 0" class="bg-white rounded-lg p-4 border border-green-200">
            <h3 class="text-lg font-semibold mb-3 text-green-800">
              {{ selectedCategory }} Kategorisindeki Kanallar ({{ filteredChannels.length }})
            </h3>
            <div class="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-4">
              <div v-for="channel in filteredChannels" :key="channel.id" class="mb-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <label class="flex items-center">
                  <input
                    type="checkbox"
                    :value="channel.id"
                    v-model="selectedChannels"
                    class="mr-3"
                  >
                  <div class="flex items-center flex-1">
                    <img
                      v-if="channel.thumbnail_url"
                      :src="channel.thumbnail_url"
                      :alt="channel.title"
                      class="w-10 h-10 rounded-full mr-3"
                    >
                    <div class="flex-1">
                      <div class="font-medium text-gray-900">{{ channel.title }}</div>
                      <div class="text-sm text-gray-500">
                        {{ channel.subscriber_count?.toLocaleString() || 0 }} abone • 
                        {{ channel.video_count || 0 }} video
                      </div>
                      <div v-if="channel.topic_categories && channel.topic_categories.length > 0" class="text-xs text-gray-400 mt-1">
                        {{ channel.topic_categories.slice(0, 2).map(cat => cat.split('/').pop()?.replace(/_/g, ' ')).join(', ') }}
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <!-- YouTube Kategorileri Listesi -->
          <div v-if="youtubeCategories.length > 0" class="bg-white rounded-lg p-4 border border-blue-200 mt-6">
            <h3 class="text-lg font-semibold mb-3 text-blue-800">YouTube Kategorileri ({{ youtubeCategories.length }})</h3>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategori Adı</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Atanabilir</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kanal ID</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="category in youtubeCategories" :key="category.id" class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {{ category.id }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {{ category.title }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span :class="category.assignable ? 'text-green-600' : 'text-red-600'">
                        {{ category.assignable ? 'Evet' : 'Hayır' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ category.channelId }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Yorum Formu -->
        <div class="bg-gray-50 rounded-lg p-6 mb-8">
          <h2 class="text-xl font-semibold mb-6">Yeni Yorum Oluştur</h2>
          
          <!-- Yorum Metni -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Yorum Metni
            </label>
            <textarea
              v-model="commentText"
              rows="4"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Bu videonun özeti için Veciz AI kullanabilirsiniz: https://veciz.ai"
            ></textarea>
            <div class="flex justify-between items-center mt-2">
              <span class="text-sm text-gray-500">
                Karakter sayısı: {{ characterCount }}/500
              </span>
              <span v-if="!isValidComment" class="text-sm text-red-500">
                Yorum 10-500 karakter arasında olmalıdır
              </span>
            </div>
          </div>

          <!-- Hedef Seçimi -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Hedef Seçimi
            </label>
            <div class="space-y-2">
              <label class="flex items-center">
                <input
                  type="radio"
                  v-model="targetType"
                  value="latest_video"
                  class="mr-2"
                >
                <span>Tüm kanalların son videolarına</span>
              </label>
              <label class="flex items-center">
                <input
                  type="radio"
                  v-model="targetType"
                  value="specific_channel"
                  class="mr-2"
                >
                <span>Belirli kanalların son videolarına</span>
              </label>
            </div>
          </div>

          <!-- Kanal Seçimi -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Kanal Listesi
            </label>
            <div v-if="isLoadingChannels" class="text-center py-4">
              <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
            </div>
            <div v-else class="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-4">
              <div v-for="channel in channels" :key="channel.id" class="mb-2">
                <label class="flex items-center">
                  <input
                    type="checkbox"
                    :value="channel.id"
                    v-model="selectedChannels"
                    class="mr-3"
                  >
                  <div class="flex items-center">
                    <img
                      v-if="channel.thumbnail_url"
                      :src="channel.thumbnail_url"
                      :alt="channel.title"
                      class="w-8 h-8 rounded-full mr-3"
                    >
                    <div>
                      <div class="font-medium">{{ channel.title }}</div>
                      <div class="text-sm text-gray-500">
                        {{ channel.subscriber_count?.toLocaleString() || 0 }} abone
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <!-- Ayarlar -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Günlük Limit
              </label>
              <input
                v-model.number="dailyLimit"
                type="number"
                min="1"
                max="50"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Yorumlar Arası Bekleme (saniye)
              </label>
              <input
                v-model.number="delaySeconds"
                type="number"
                min="10"
                max="120"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
            </div>
            
            <div class="flex items-center">
              <label class="flex items-center">
                <input
                  v-model="isTestMode"
                  type="checkbox"
                  class="mr-2"
                >
                <span class="text-sm font-medium text-gray-700">Test Modu</span>
              </label>
            </div>
          </div>

          <!-- Butonlar -->
          <div class="flex space-x-4">
            <button
              @click="sendComments"
              :disabled="isLoading || !isValidComment || selectedChannels.length === 0"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <div v-if="isLoading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {{ isTestMode ? 'Test Et' : 'Yorumları Gönder' }}
            </button>
            
            <button
              @click="clearForm"
              class="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Temizle
            </button>
          </div>
        </div>

        <!-- Yorum Geçmişi -->
        <div class="bg-white rounded-lg border">
          <div class="px-6 py-4 border-b">
            <h2 class="text-xl font-semibold">Yorum Geçmişi</h2>
          </div>
          
          <div v-if="isLoadingHistory" class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          </div>
          
          <div v-else-if="commentHistory.length === 0" class="text-center py-8 text-gray-500">
            Henüz yorum gönderilmemiş
          </div>
          
          <div v-else class="divide-y">
            <div
              v-for="comment in commentHistory"
              :key="comment.id"
              class="px-6 py-4 hover:bg-gray-50"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center space-x-2 mb-2">
                    <span class="text-sm font-medium text-gray-900">
                      {{ comment.comment_text.substring(0, 100) }}{{ comment.comment_text.length > 100 ? '...' : '' }}
                    </span>
                    <span :class="getStatusColor(comment.status)" class="text-sm font-medium">
                      {{ getStatusText(comment.status) }}
                    </span>
                  </div>
                  
                  <div class="text-sm text-gray-500">
                    <span>{{ comment.target_type === 'latest_video' ? 'Son videolar' : 'Belirli kanallar' }}</span>
                    <span class="mx-2">•</span>
                    <span>{{ comment.channel_ids?.length || 0 }} kanal</span>
                    <span class="mx-2">•</span>
                    <span>{{ formatDate(comment.created_at) }}</span>
                  </div>
                  
                  <!-- Sonuçlar -->
                  <div v-if="comment.results && comment.results.length > 0" class="mt-3">
                    <div class="text-sm text-gray-600 mb-2">Sonuçlar:</div>
                    <div class="space-y-1">
                      <div
                        v-for="(result, index) in comment.results.slice(0, 3)"
                        :key="index"
                        class="text-xs text-gray-500"
                      >
                        {{ result.channel_title || result.channel_id }} - 
                        {{ result.video_title || result.video_id }} - 
                        <span :class="result.status === 'success' ? 'text-green-600' : 'text-red-600'">
                          {{ result.status === 'success' ? 'Başarılı' : 'Başarısız' }}
                        </span>
                      </div>
                      <div v-if="comment.results.length > 3" class="text-xs text-gray-400">
                        +{{ comment.results.length - 3 }} daha...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- OAuth2 Kurulum Modal -->
    <div v-if="showOAuthSetup" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-2xl font-bold text-gray-900">Veciz AI - OAuth2 Kurulum Rehberi</h2>
          <button
            @click="showOAuthSetup = false"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div class="prose max-w-none">
          <h3 class="text-lg font-semibold mb-4">🎯 Yapılacaklar Listesi</h3>
          
          <div class="space-y-4">
            <div class="bg-blue-50 p-4 rounded-lg">
              <h4 class="font-semibold text-blue-800 mb-2">1. Email Hesabı Oluşturma</h4>
              <ul class="list-disc list-inside text-sm text-blue-700 space-y-1">
                <li>✅ `veciz.ai@gmail.com` hesabı açın</li>
                <li>✅ Veya `info@veciz.ai` (domain email)</li>
                <li>✅ Profesyonel profil bilgileri ekleyin</li>
              </ul>
            </div>

            <div class="bg-green-50 p-4 rounded-lg">
              <h4 class="font-semibold text-green-800 mb-2">2. YouTube Kanalı Oluşturma</h4>
              <ul class="list-disc list-inside text-sm text-green-700 space-y-1">
                <li>✅ Yeni YouTube kanalı açın: "Veciz AI"</li>
                <li>✅ Profesyonel kanal açıklaması yazın</li>
                <li>✅ Veciz AI logosu ve banner ekleyin</li>
                <li>✅ Birkaç video yükleyin (AI, tech konuları)</li>
              </ul>
            </div>

            <div class="bg-purple-50 p-4 rounded-lg">
              <h4 class="font-semibold text-purple-800 mb-2">3. Google Cloud Console Ayarları</h4>
              <ul class="list-disc list-inside text-sm text-purple-700 space-y-1">
                <li>✅ https://console.cloud.google.com/ adresine gidin</li>
                <li>✅ Proje: `veciz2` seçin</li>
                <li>✅ "APIs & Services" > "OAuth consent screen"</li>
                <li>✅ App name: "Veciz AI" yapın</li>
                <li>✅ User support email: `veciz.ai@gmail.com` ekleyin</li>
                <li>✅ Test users'a `veciz.ai@gmail.com` ekleyin</li>
              </ul>
            </div>

            <div class="bg-orange-50 p-4 rounded-lg">
              <h4 class="font-semibold text-orange-800 mb-2">4. Yeni OAuth2 Token Oluşturma</h4>
              <div class="bg-gray-100 p-3 rounded text-sm font-mono text-orange-700">
                # Backend klasörüne gidin<br>
                cd backend<br>
                # OAuth2 token oluşturun<br>
                node create_oauth_token.js<br>
                # Yeni email ile authorization yapın<br>
                # Token'ı kaydedin
              </div>
            </div>

            <div class="bg-red-50 p-4 rounded-lg">
              <h4 class="font-semibold text-red-800 mb-2">5. Test</h4>
              <ul class="list-disc list-inside text-sm text-red-700 space-y-1">
                <li>✅ Admin panel'e gidin: http://localhost:5173/admin/youtube-comments</li>
                <li>✅ Yeni token ile test edin</li>
                <li>✅ Başarılı olursa eski token'ı silin</li>
              </ul>
            </div>
          </div>

          <h3 class="text-lg font-semibold mt-6 mb-4">📋 Kanal Açıklaması Örneği</h3>
          <div class="bg-gray-100 p-4 rounded-lg text-sm">
            <pre class="whitespace-pre-wrap">Veciz AI - AI-Powered Video Summarization Platform

Transform your YouTube videos into concise summaries with our advanced AI technology. 
Get instant insights from long-form content.

🔗 Website: https://veciz.ai
📧 Contact: info@veciz.ai
🤖 Powered by AI</pre>
          </div>

          <h3 class="text-lg font-semibold mt-6 mb-4">⚠️ Önemli Notlar</h3>
          <ul class="list-disc list-inside text-sm text-gray-700 space-y-1">
            <li>Yeni email ile OAuth2 token oluşturduktan sonra eski token'ı silin</li>
            <li>Test kullanıcısı olarak yeni email'i ekleyin</li>
            <li>Kanal açıklamasında Veciz AI'ı tanıtın</li>
            <li>Profesyonel görünüm için birkaç video yükleyin</li>
          </ul>
        </div>

        <div class="flex justify-end mt-6">
          <button
            @click="showOAuthSetup = false"
            class="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
